import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, DownloadIcon } from './Icons';

// Plays a video asset in a popup. Uses the browser's own decoder, so a video the
// browser can't decode shows a download fallback instead of a blank player.
function VideoPlayerModal({ asset, onClose }) {
    const [unsupported, setUnsupported] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    // The popup can sit inside clickable rows (e.g. the asset picker), and React
    // bubbles events from a portal to its parents, so keep clicks contained.
    return createPortal(
        <div
            role='dialog'
            aria-modal='true'
            aria-label={asset.name}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 md:p-6'
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='w-full md:max-w-[960px] bg-white md:rounded-lg shadow-xl overflow-hidden'
            >
                <div className='flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200'>
                    <p className='min-w-0 truncate font-medium text-gray-700' title={asset.name}>{asset.name}</p>
                    <div className='flex items-center shrink-0 gap-2'>
                        <a
                            href={asset.source}
                            target='_blank'
                            rel='noopener noreferrer'
                            download
                            title='Download'
                            aria-label='Download'
                            className='p-1.5 rounded-md hover:bg-slate-100'
                        >
                            <DownloadIcon size='w-5 h-5' />
                        </a>
                        <button
                            type='button'
                            onClick={onClose}
                            title='Close'
                            aria-label='Close'
                            className='p-1.5 rounded-md text-gray-600 hover:bg-slate-100'
                        >
                            <CloseIcon size='w-5 h-5' />
                        </button>
                    </div>
                </div>
                {unsupported ? (
                    <div className='flex flex-col items-center gap-4 px-6 py-12 text-center'>
                        <p className='text-gray-700'>
                            This video can't be played in your browser. Download it to watch it in a video player.
                        </p>
                        <a
                            href={asset.source}
                            target='_blank'
                            rel='noopener noreferrer'
                            download
                            className='bg-orange-400 hover:bg-orange-500 text-white text-sm md:text-base font-medium px-6 py-2 md:py-3 rounded-md'
                        >
                            Download Video
                        </a>
                    </div>
                ) : (
                    <div className='relative bg-black'>
                        <video
                            src={asset.source}
                            controls
                            autoPlay
                            playsInline
                            preload='metadata'
                            onError={() => setUnsupported(true)}
                            // A track the browser can't decode can leave audio with no picture.
                            onLoadedData={(e) => { if (e.currentTarget.videoWidth === 0) setUnsupported(true); else setLoading(false); }}
                            onWaiting={() => setLoading(true)}
                            onPlaying={() => setLoading(false)}
                            className='block w-full max-h-[75vh] bg-black'
                        />
                        {loading && (
                            <div className='absolute inset-0 flex flex-col items-center justify-center gap-3 text-white pointer-events-none'>
                                <div className='h-10 w-10 rounded-full border-4 border-white/30 border-t-white animate-spin' />
                                <p className='text-sm'>Loading video...</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default VideoPlayerModal;
